/**
 * Load/save provider weekly availability on a FHIR Schedule resource.
 */
import type { MedplumClient } from '@medplum/core';
import type { Schedule } from '@medplum/fhirtypes';
import {
  applyProviderPlanningHorizon,
  type DayOfWeekCode,
  DAY_OF_WEEK_CODES,
  getWeeklyAvailability,
  localWallTimeToUtcValueTime,
  setWeeklyAvailability,
  utcValueTimeToLocalWallTime,
  type WeeklyAvailabilityPattern,
} from '@unify/fhir-model/resources/schedule';
import {
  getDefaultProviderWeeklyAvailability,
  PROVIDER_AVAILABILITY_UI_DAYS,
  type ProviderAvailabilityUiDay,
  type ProviderWeeklyAvailability,
} from './providerAvailabilityTypes';
import { stampProviderScheduleAccounts } from '../../lib/scheduleAppointmentService';

export { buildProviderPlanningHorizon } from '@unify/fhir-model/resources/schedule';

const UI_DAY_TO_CODE: Record<ProviderAvailabilityUiDay, DayOfWeekCode> = {
  Sun: 'sun',
  Mon: 'mon',
  Tue: 'tue',
  Wed: 'wed',
  Thu: 'thu',
  Fri: 'fri',
  Sat: 'sat',
};

const CODE_TO_UI_DAY: Record<DayOfWeekCode, ProviderAvailabilityUiDay> = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
};

/** Default IANA timezone when practice timezone is unknown. */
export const DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE = 'America/Denver';

function parseTimeLabel12h(label: string): { hour: number; minute: number } | null {
  const match = label.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return { hour, minute };
}

function formatTimeLabel12h(hour: number, minute: number): string {
  const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  const period = hour >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
}

export function uiWeeklyAvailabilityToPattern(
  ui: ProviderWeeklyAvailability,
  timeZone: string,
): WeeklyAvailabilityPattern {
  const pattern: WeeklyAvailabilityPattern = {};
  for (const uiDay of PROVIDER_AVAILABILITY_UI_DAYS) {
    const dayData = ui[uiDay];
    if (!dayData?.enabled) continue;
    const slot = dayData.slots[0];
    if (!slot?.start || !slot?.end) continue;
    const startP = parseTimeLabel12h(slot.start);
    const endP = parseTimeLabel12h(slot.end);
    if (!startP || !endP) continue;
    const code = UI_DAY_TO_CODE[uiDay];
    pattern[code] = [
      {
        start: localWallTimeToUtcValueTime(code, startP.hour, startP.minute, timeZone),
        end: localWallTimeToUtcValueTime(code, endP.hour, endP.minute, timeZone),
      },
    ];
  }
  return pattern;
}

export function patternToUiWeeklyAvailability(
  pattern: WeeklyAvailabilityPattern,
  timeZone: string,
): ProviderWeeklyAvailability {
  const ui = getDefaultProviderWeeklyAvailability();
  for (const code of DAY_OF_WEEK_CODES) {
    const uiDay = CODE_TO_UI_DAY[code];
    const windows = pattern[code];
    if (!windows?.length) {
      ui[uiDay] = { enabled: false, slots: [] };
      continue;
    }
    const window = windows[0];
    const startLocal = utcValueTimeToLocalWallTime(code, window.start, timeZone);
    const endLocal = utcValueTimeToLocalWallTime(code, window.end, timeZone);
    if (!startLocal || !endLocal) {
      ui[uiDay] = { enabled: false, slots: [] };
      continue;
    }
    ui[uiDay] = {
      enabled: true,
      slots: [
        {
          start: formatTimeLabel12h(startLocal.hour, startLocal.minute),
          end: formatTimeLabel12h(endLocal.hour, endLocal.minute),
        },
      ],
    };
  }
  return ui;
}

export async function searchProviderSchedule(
  medplum: MedplumClient,
  practitionerId: string,
): Promise<Schedule | undefined> {
  medplum.invalidateSearches('Schedule');
  const schedules = await medplum.searchResources('Schedule', {
    actor: `Practitioner/${practitionerId}`,
    _count: '10',
  });
  return schedules[0];
}

export async function loadProviderWeeklyAvailability(
  medplum: MedplumClient,
  practitionerId: string,
  timeZone: string = DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE,
): Promise<{ schedule: Schedule | undefined; weekly: ProviderWeeklyAvailability }> {
  const schedule = await searchProviderSchedule(medplum, practitionerId);
  if (!schedule) {
    return { schedule: undefined, weekly: getDefaultProviderWeeklyAvailability() };
  }
  const pattern = getWeeklyAvailability(schedule);
  const hasAny = Object.keys(pattern).length > 0;
  return {
    schedule,
    weekly: hasAny ? patternToUiWeeklyAvailability(pattern, timeZone) : getDefaultProviderWeeklyAvailability(),
  };
}

export async function saveProviderWeeklyAvailability(
  medplum: MedplumClient,
  practitionerId: string,
  draft: ProviderWeeklyAvailability,
  options?: {
    timeZone?: string;
    practitionerDisplay?: string;
    existingSchedule?: Schedule;
  },
): Promise<Schedule> {
  const timeZone = options?.timeZone ?? DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE;
  const pattern = uiWeeklyAvailabilityToPattern(draft, timeZone);
  let schedule =
    options?.existingSchedule ?? (await searchProviderSchedule(medplum, practitionerId));

  if (!schedule) {
    schedule = {
      resourceType: 'Schedule',
      active: true,
      actor: [
        {
          reference: `Practitioner/${practitionerId}`,
          ...(options?.practitionerDisplay ? { display: options.practitionerDisplay } : {}),
        },
      ],
    };
  }

  let updated = setWeeklyAvailability({ ...schedule, active: true }, pattern);
  updated = applyProviderPlanningHorizon(updated);

  let saved: Schedule = updated.id
    ? await medplum.updateResource(updated)
    : await medplum.createResource(updated);

  if (saved.id) {
    saved = await stampProviderScheduleAccounts(medplum, saved.id, practitionerId);
  }

  medplum.invalidateSearches('Schedule');
  return saved;
}
